/**
 * SvgDividerPicker — the DIVIDER_SVG_STYLES_V3 family-grouped style grid.
 *
 * Rendered ONLY when DIVIDER_SVG_STYLES_V3_ENABLED is on (the OFF path keeps the
 * legacy 4-box CSS picker in ./index.jsx byte-identical). Mirrors the reviewed
 * design deck (design-handoff/harness/elevations/ewflow-picker-text.elevation.mjs
 * DV IIFE): a scrollable panel of sticky family headers + a 3-col thumbnail grid,
 * each tile a center-cropped inline-SVG preview built by the pure svgDividers
 * library (geometry generated in-code, color sanitized before injection).
 *
 * Tokens are the deck's own (deck governs): DM Sans, ink #123051, selection
 * #8F38EF, teal animatable dot #0FADAD. The thumbnails never animate (built with
 * animate:false) so the picker is motion-free; block-level drift honours
 * prefers-reduced-motion in the block itself.
 */
import React, { useRef } from "react";
import styled from "styled-components";
import { buildDividerSVG, dividerStylesByFamily } from "./svgDividers";
import { focusRing } from "../../../../theme/tokens";

const Wrap = styled.div`
  font-family: "DM Sans", sans-serif;
  color: #123051;
  margin-top: 12px;
`;

const Label = styled.div`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: #565556;
  margin-bottom: 6px;
`;

const Scroll = styled.div`
  max-height: 300px;
  overflow-y: auto;
  padding: 0 2px 4px 0;
  border: 1px solid #eceaf2;
  border-radius: 8px;
  background: #fbfafe;
  &::-webkit-scrollbar {
    width: 8px;
  }
  &::-webkit-scrollbar-thumb {
    background: #d9ccf2;
    border-radius: 4px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
`;

const FamHead = styled.div`
  display: flex;
  align-items: center;
  gap: 7px;
  flex-wrap: wrap;
  padding: 9px 9px 5px;
  position: sticky;
  top: 0;
  background: #fbfafe;
  z-index: 1;
  border-bottom: 1px solid #f0ebfa;
  .t {
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.07em;
    text-transform: uppercase;
    color: #565556;
  }
`;

const Badge = styled.span`
  font-size: 9px;
  font-weight: 700;
  color: #8f38ef;
  background: #f2ecfd;
  border: 1px solid #e4d5fb;
  padding: 1px 6px;
  border-radius: 999px;
  line-height: 1.5;
`;

const AnimBadge = styled(Badge)`
  color: #0a6e8a;
  background: #eaf8f8;
  border-color: #cbeded;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  padding: 8px 9px 10px;
`;

const Tile = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 60px;
  padding: 7px 9px;
  background: #fff;
  border: ${({ $on }) => ($on ? "2px solid #8f38ef" : "1px solid #e1dcec")};
  background: ${({ $on }) => ($on ? "#f7f2fe" : "#fff")};
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  outline: none;
  &:hover {
    border-color: #c9bce8;
    background: ${({ $on }) => ($on ? "#f7f2fe" : "#fcfaff")};
  }
  &:focus-visible {
    box-shadow: ${focusRing.shadow};
  }
  .dv-svgwrap {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
  }
  .dv-svgwrap svg {
    width: 100%;
    height: auto;
    max-height: 46px;
    max-width: 100%;
  }
`;

const AnimDot = styled.span`
  position: absolute;
  top: 4px;
  right: 4px;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: #0fadad;
  box-shadow: 0 0 0 1.5px #fff;
`;

const Note = styled.p`
  font-size: 10.5px;
  color: #9a999a;
  margin: 8px 2px 2px;
  line-height: 1.5;
`;

const capsText = (style) => {
  const two = (style.lines || 1) >= 2;
  return [
    style.anim ? "animatable" : "static",
    style.asym ? "flippable" : null,
    two ? "per-line color" : null,
  ]
    .filter(Boolean)
    .join(" · ");
};

const SvgDividerPicker = ({ selectedStyle, onSelect, previewColor = "#123051" }) => {
  const families = dividerStylesByFamily();
  const gridRef = useRef(null);

  // Roving keyboard nav across the whole grid (radiogroup semantics).
  const flat = families.reduce((acc, fam) => acc.concat(fam.items), []);
  const handleKeyDown = (e, id) => {
    if (e.key === "Enter" || e.key === " " || e.key === "Spacebar") {
      e.preventDefault();
      onSelect(id);
      return;
    }
    const idx = flat.findIndex((s) => s.id === id);
    let next = null;
    if (e.key === "ArrowRight" || e.key === "ArrowDown") next = flat[idx + 1];
    else if (e.key === "ArrowLeft" || e.key === "ArrowUp") next = flat[idx - 1];
    if (next) {
      e.preventDefault();
      onSelect(next.id);
      const el = gridRef.current?.querySelector(`[data-style-id="${next.id}"]`);
      if (el) el.focus();
    }
  };

  return (
    <Wrap>
      <Label>Style</Label>
      <Scroll ref={gridRef} role="radiogroup" aria-label="Divider style">
        {families.map((fam) => (
          <React.Fragment key={fam.key}>
            <FamHead>
              <span className="t">{fam.label}</span>
              <Badge>{fam.items.length}</Badge>
              {fam.animCount ? <AnimBadge>{fam.animCount} anim</AnimBadge> : null}
            </FamHead>
            <Grid>
              {fam.items.map((style) => {
                const on = style.id === selectedStyle;
                return (
                  <Tile
                    key={style.id}
                    $on={on}
                    data-style-id={style.id}
                    role="radio"
                    aria-checked={on}
                    aria-label={`${style.name} — ${capsText(style)}`}
                    title={`${style.name} · ${capsText(style)}`}
                    tabIndex={0}
                    onClick={() => onSelect(style.id)}
                    onKeyDown={(e) => handleKeyDown(e, style.id)}
                  >
                    {style.anim ? <AnimDot aria-hidden="true" title="animatable" /> : null}
                    <span
                      className="dv-svgwrap"
                      aria-hidden="true"
                      dangerouslySetInnerHTML={{
                        __html: buildDividerSVG(style.id, {
                          color: previewColor,
                          thickness: 3,
                          zoom: 2.8,
                          uid: `pick_${style.id}`,
                        }),
                      }}
                    />
                  </Tile>
                );
              })}
            </Grid>
          </React.Fragment>
        ))}
      </Scroll>
      <Note>
        <b>{flat.length} styles</b> in 7 families &mdash; scroll to browse. Every style is a crisp
        vector. A teal dot marks styles that can gently drift (Animation, in Advanced).
      </Note>
    </Wrap>
  );
};

export default SvgDividerPicker;
